import { inject } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';
import { Router } from '@angular/router';
import { Location } from '@freelancer/location';
import { Pwa } from '@freelancer/pwa';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import type { UiConfig } from '@freelancer/ui/ui.interface';
import { of } from 'rxjs';

export const RedirectToThemeGuard = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): ReturnType<CanActivateFn> => {
  const location = inject(Location);
  const router = inject(Router);
  const uiConfig = inject(UI_CONFIG);
  const pwa = inject(Pwa);

  const redirectToThemes = route.data
    ?.redirectToThemeFilter as readonly UiConfig['theme'][];
  if (
    (uiConfig.theme && redirectToThemes === undefined) ||
    redirectToThemes?.includes(uiConfig.theme)
  ) {
    /**
     * FIXME: T283202 - Using skipLocationChange in a guard causes the scroll position to not be restored correctly on back navigation.
     * We use skipLocationChange so the browser URL isn't updated as we dont want the theme prefixed, (e.g. `/projects` instead of `/freightlancer/projects`).
     * This is not something can currently be done in an elegant way, the following solution is from  https://github.com/angular/angular/issues/27148#issuecomment-831414318
     * As a workaround to allow the scroll position to be restored on the capacitor app, we dont use skipLocationChange on the apps as the URL is not exposed to the user anyways.
     */
    const currentNavigation = router.getCurrentNavigation();
    if (currentNavigation && !pwa.isNative()) {
      currentNavigation.extras.skipLocationChange = true;
    }
    // Clear fragments and query params from url and specify them in extras
    const urlTree = router.parseUrl(`${state.url}`);
    const urlWithoutParams =
      urlTree.root.children.primary?.segments.map(it => it.path).join('/') ??
      '';

    return location.createUrlTree([`/${uiConfig.theme}/${urlWithoutParams}`], {
      queryParams: route.queryParams,
      fragment: route.fragment ?? undefined,
      skipSSRRedirection: true,
    });
  }
  return of(true);
};
